import React, {ReactNode, useEffect, useReducer} from 'react';
import styles from './month-view.module.scss';
import moment from 'moment';
import DayIcon from '../../../day-icon';
import classNames from 'classnames';
import {
    Day,
    InRangeResult, Month,
    MonthDayCell, MonthViewBodyProps,
    MonthViewProps,
    MonthViewReducerState,
    MonthViewReducerUpdateAction,
    MonthViewReducerUpdateActionType, MonthViewRowCellProps, MonthViewRowProps
} from '../../types';
import {isMonthDayCellArray} from '../../utils';

const TOTAL_DAYS = 7;

const MonthRowCell = (props: MonthViewRowCellProps) => {
    const isInRangeResult = props.isInRange?.();
    return(<div className={classNames({
        [styles.cell]: true,
        [styles.day]: props.isDay,
        [styles.rangeSelected]: isInRangeResult ? isInRangeResult.commit : false,
        [styles.rangePreview]: isInRangeResult ? !isInRangeResult.commit : false,
        [styles.start]: isInRangeResult ? isInRangeResult.start : false,
        [styles.end]: isInRangeResult ? isInRangeResult.end : false,
        [styles.rangeEnabled]: props.isRangeEnabled && props.isDay,
        [styles.header]: props.header && props.header,
        [styles.small]: props.small,
    })} onMouseEnter={() => props.onOver?.()} onMouseLeave={() => props.onLeave?.()}>
        {props.children}
    </div>)
}

const MonthRow = (props: MonthViewRowProps) => {
    return (<>
        {
            isMonthDayCellArray(props.children) ? 
                props.children.map((x, index) => (
                    <MonthRowCell key={index}
                        header={props.header}
                        isDay={x.id !== 0}
                        isRangeEnabled={props.isRangeEnabled}
                        onOver={() => props.onDayOver?.(x.date)}
                        onLeave={() => props.onDayLeave?.(x.date)}
                        isInRange={() => props.isInRange(x.date)}
                        small={props.small}>{
                            x.content
                        }</MonthRowCell>))
                : props.children.map((x, index) =>
                    (<MonthRowCell key={index} header={props.header} isDay={false} isRangeEnabled={false} small={props.small}>{x}</MonthRowCell>))
        }
    </>)
}

const WeekDaysRow = (props: { small: boolean }) => {
    return(<MonthRow header={true} isRangeEnabled={false} isInRange={() => undefined} small={props.small}>{ moment.weekdaysShort(true).map((day, index) => (<span key={index} className={styles.weekDay}>{day}</span>)) }</MonthRow>)
}

/**
 * Example
 * MAY 2024
 * [
 *     ['','', 1, 2, 3, 4, 5]
 *     [ 6, 7, 8, 9,10,11,12]
 *     [13,14,15,16,17,18,19]
 *     [20,21,22,23,24,25,26]
 *     [27,28,29,30,31,'','']
 * ]
 */
const MonthBody = (props: MonthViewBodyProps) => {
    const all = [...props.startBlanks, ...props.days, ...props.endBlanks];
    const rows: Array<ReactNode> = [];

    let rowCells: Array<MonthDayCell> = [];
    all.forEach((element, i) => {
        if (i % TOTAL_DAYS !== 0) {
            rowCells.push(element);
        } else {
            if (rowCells.length > 0) {
                rows.push(<MonthRow key={i} isRangeEnabled={props.isRangeEnabled} isInRange={props.isInRange} onDayOver={props.onDayOver} onDayLeave={props.onDayLeave} small={props.small}>
                    {rowCells}
                </MonthRow>)
            }
            rowCells = [];
            rowCells.push(element);
        }

        if (i == all.length - 1) {
            rows.push(<MonthRow key={i} isRangeEnabled={props.isRangeEnabled} isInRange={props.isInRange} onDayOver={props.onDayOver} onDayLeave={props.onDayLeave} small={props.small}>
                {rowCells}
            </MonthRow>)
        }
    })

    return(<>{ rows }</>);
}

const getBlankDays = (dayOfWeek: number, lastDay: boolean): Array<MonthDayCell> => {
    const blanks: MonthDayCell[] = []
    for (let i = 0; i < (!lastDay ? dayOfWeek < 1 ? 6 : dayOfWeek - 1 : dayOfWeek < 1 ? 0 : TOTAL_DAYS - dayOfWeek); i++) {
        blanks.push({
            id: 0,
            content: <span key={i}></span>
        })
    }
    return blanks;
}

const getDayType = (day?: Day): 'normal' | 'info' | 'warning' => {
    if (!day) {
        return 'normal'
    }

    switch (day.type) {
        case 'default': 
            return 'normal';
        case 'short': 
            return 'info';
        case 'off':
            return 'warning'
    }
}

const getDaysInMonth = (
    date: moment.Moment,
    onClick: (date: moment.Moment) => void,
    small: boolean,
    month?: Month,
    minDate?: moment.Moment,
    maxDate?: moment.Moment) => {
    const yearId = date.year();
    const monthId = date.month();
    const days: MonthDayCell[] = [];
    for (let i = 1; i <= date.daysInMonth(); i++) {
        const transformedDate = moment({ year: date.year(), month: date.month(), date: i }).startOf('day');
        const day = month?.days.find(d => d.id === i);
        const isLess = minDate ? minDate.diff({ year: yearId, month: monthId, date: i }, 'days') > 0 : false
        const isGreater = maxDate ? maxDate.diff({ year: yearId, month: monthId, date: i }, 'days') < 0 : false
        const type = getDayType(day);
        days.push({
            id: i,
            content: <DayIcon
                key={transformedDate.unix()}
                day={i}
                onClick={() => onClick(transformedDate)}
                type={type}
                disabled={isLess || isGreater}
                small={small} />,
            date: transformedDate
        });
    }

    return days;
}

const MonthView = (props: MonthViewProps) => {
    const { activeDate, month, range, small, minDate, maxDate, onDayClick, getDayIsInRange } = props;
    const { onDayOverEnter, onDayOverLeave } = props;
    const isRangeEnabled = range !== undefined;
    const firstDay = (moment(activeDate).clone().startOf('month')).day();
    const lastDay = (moment(activeDate).clone().endOf('month')).day();
    
    const initialState: MonthViewReducerState = {
        days: getDaysInMonth(activeDate, onDayClick, !!small, month, minDate, maxDate),
        startBlankDays: getBlankDays(firstDay, false),
        endBlankDays: getBlankDays(lastDay, true)
    }
    
    const [monthState, monthDispatch] = useReducer((state: MonthViewReducerState, action: Partial<MonthViewReducerUpdateAction>) => {
        if (action.type === MonthViewReducerUpdateActionType.RELOAD_DAYS) {
            return { ...state, days: getDaysInMonth(activeDate, onDayClick, !!small, month, minDate, maxDate)};
        } else if (action.type === MonthViewReducerUpdateActionType.FULL_RELOAD) {
            return {
                days: getDaysInMonth(activeDate, onDayClick, !!small, month, minDate, maxDate),
                startBlankDays: getBlankDays(firstDay, false),
                endBlankDays: getBlankDays(lastDay, true)
            }
        }
        return state;
    }, initialState)

    useEffect(() => {
        monthDispatch({ type: MonthViewReducerUpdateActionType.RELOAD_DAYS })
    }, [range]);

    useEffect(() => {
        monthDispatch({ type: MonthViewReducerUpdateActionType.FULL_RELOAD })
    }, [month]);

    const handleIsDayInRange = (day?: moment.Moment): InRangeResult | undefined => {
        if (!day || !day.isValid()) {
            return undefined;
        }

        return getDayIsInRange(day);
    }

    const handleDayOver = (day?: moment.Moment) => {
        if (!day) return;

        onDayOverEnter(day);
    }

    const handleDayLeave = (day?: moment.Moment) => {
        if (!day) return;
        onDayOverLeave(day)
    }

    return(<div className={classNames(styles.grid, {
        [styles.dense]: props.dense
    })}>
        <WeekDaysRow small={!!small}/>
        { monthState &&
            <MonthBody startBlanks={monthState.startBlankDays} 
                days={monthState.days} 
                endBlanks={monthState.endBlankDays} 
                isRangeEnabled={isRangeEnabled} 
                isInRange={handleIsDayInRange}
                onDayOver={handleDayOver}
                onDayLeave={handleDayLeave}
                small={!!small}/> }
    </div>)
}

export default React.memo(MonthView);
